import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Divider } from '@material-ui/core';
import { Link, useHistory, useParams } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { del, get, put } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 20,
        marginBottom: 24
    },
    updateBtn: {
        ...themeColors.yellowBtn
    },
    addBtn: {
        marginLeft: 20,
        padding: '.4rem .65rem',
        ...themeColors.greenBtn
    },
    removeBtn: {
        padding: '.4rem .65rem',
        ...themeColors.redBtn
    },
    tableContainer: {
        width: '100%',
        marginBottom: 24
    }
}));

const GroupDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const { id } = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedPolicies, setSelectedPolicies] = useState([]);
    const [state, setState] = useState({
        loading: true,
        group: {
            name: '',
            description: ''
        },
        users: [],
        policies: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'User group detail | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/iamGroup/${id}`);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                group: response.data,
                users: response.users,
                policies: response.policies
            }));
        } else {
            console.log('[GroupDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, group: { ...prev.group, [target.name]: target.value } }));

    const validate = type => {
        if (type === 'update') {
            const { name } = state.group;
            const regex = /^[a-zA-Z0-9+=,.@-_]+$/;

            if (!name.trim()) {
                showToast('Name field is required', 'error');
                return false;
            }
            if (!regex.test(name)) {
                showToast(`Name field: use alphanumeric and '+=,.@-' characters`, 'error', 6000);
                return false;
            }

            return true;
        }

        if (type === 'user') {
            if (selectedUsers.length <= 0) {
                showToast('Select some user');
                return false;
            }

            return true;
        }

        if (type === 'policy') {
            if (selectedPolicies.length <= 0) {
                showToast('Select some policy');
                return false;
            }

            return true;
        }
    }

    const update = async () => {
        const cond = validate('update');
        if (cond) {
            showLoader(true);
            const response = await put(`/iamGroup/${id}/update`, state.group);
            if (response.status === 200) {
                showToast(response.message);
            } else {
                console.log('[GroupDetail] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const remove = async type => {
        const cond = validate(type);
        if (cond) {
            showLoader(true);
            const response = await del(`/iamGroup/${id}/removeData`, { selectedUsers, selectedPolicies, type });
            if (response.status === 200) {
                if (type === 'user') setState(prev => ({ ...prev, users: response.data }));
                if (type === 'policy') setState(prev => ({ ...prev, policies: response.data }));

                showToast(response.message);
            } else {
                console.log('[GroupDetail] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const getPolicyType = ({ value }) => (value) ? 'CFG managed' : 'User managed';

    const userColumns = [
        { field: 'username', headerName: 'Username', flex: 1, minWidth: 200 },
        { field: 'names', headerName: 'Names', flex: 1, minWidth: 200 },
        { field: 'surnames', headerName: 'Surnames', flex: 1, minWidth: 200 }
    ];

    const policyColumns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        { field: 'default', headerName: 'Type', flex: 1, minWidth: 200, valueGetter: getPolicyType },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 }
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <h2>{'Detail'}</h2>

            <Grid container>
                <Grid item xs={12} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Name'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Name'
                                name='name'
                                value={state.group.name}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={9}>
                    <FormGroup>
                        <FormLabel>{'Description'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Description'
                                name='description'
                                value={state.group.description}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>
            <div className={classes.controls}>
                <Button
                    variant='contained'
                    className={classes.updateBtn}
                    onClick={update}
                >
                    {'Update'}
                </Button>
            </div>

            <Divider className={classes.divider} />

            <div className={classes.tableContainer}>
                <div className={classes.row}>
                    <h2>{'Users in this group'}</h2>

                    <div>
                        <Button
                            variant='contained'
                            className={classes.removeBtn}
                            disabled={!selectedUsers.length}
                            onClick={() => remove('user')}
                        >
                            {'Remove users'}
                        </Button>
                        {/* <Button
                            variant='contained'
                            className={classes.addBtn}
                            // onClick={() => remove('user')}
                        >
                            {'Add users'}
                        </Button> */}
                    </div>
                </div>

                <DataGrid
                    rows={state.users}
                    columns={userColumns}
                    pageSize={10}
                    density='compact'
                    autoHeight
                    getRowId={row => row.iduser}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={array => setSelectedUsers(array)}
                />
            </div>

            <Divider className={classes.divider} />

            <div className={classes.tableContainer}>
                <div className={classes.row}>
                    <h2>{'Policies attached'}</h2>

                    <div>
                        <Button
                            variant='contained'
                            className={classes.removeBtn}
                            disabled={!selectedPolicies.length}
                            onClick={() => remove('policy')}
                        >
                            {'Remove policies'}
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.addBtn}
                            onClick={() => history.push(`/iam/groups/detail/${id}/add-policies`)}
                        >
                            {'Add policies'}
                        </Button>
                    </div>
                </div>

                <DataGrid
                    rows={state.policies}
                    columns={policyColumns}
                    pageSize={20}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idiam_policy}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={array => setSelectedPolicies(array)}
                />
            </div>

        </div>
    );
}

export default GroupDetail;